import { useGetRegionalResponsibleQuery } from "@/services/analyst-assignment/select-options";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { SelectInput } from "@/components/Select";
import { DateRangePicker } from "components/ui/date-range-picker";
import { Separator } from "components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "components/ui/sheet";
import { Filter, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useConciliationEmailsFilterActions } from "../filter/hooks/actions";
import { useFilterState } from "../filter/hooks/filterState";
import { ConciliationEmailReportType, LIST_TYPES, EMAIL_TYPE_OPTIONS, ITEM_STATUS_OPTIONS, AGGREGATED_STATUS_OPTIONS } from "./conciliation-email-report-types";
import { getSelectClasses } from "@/utils/filter-styles";
import { MultiValueTag } from "@/components/ui/MultiValueTag";
import { formatOptionLabel } from "@/utils/format-option-label";

export default function EmailReportFilters() {
  const { updateFilter, clearAllFilters } = useConciliationEmailsFilterActions();
  const { data: storeOptions } = useGetStoresOptionsQuery()
  const { data: regionalResponsibleOptions } = useGetRegionalResponsibleQuery()
  const { filters } = useFilterState()

  const [listType, setListType] = useState<ConciliationEmailReportType>(
    (filters?.reportType as ConciliationEmailReportType) || ConciliationEmailReportType.EMAILS_BY_STORE,
  )

  useEffect(() => {
    if (filters?.store && filters.store !== filters.storeCode) {
      updateFilter({ key: "storeCode", value: filters.store })
    }
  }, [filters?.store, filters?.storeCode, updateFilter])

  useEffect(() => {
    if (filters?.reportType && filters.reportType !== listType) {
      setListType(filters.reportType as ConciliationEmailReportType)
    }
  }, [filters?.reportType, listType])

  const handleTypeChange = (value: ConciliationEmailReportType) => {
    setListType(value)
    updateFilter({ key: "reportType", value })
  }

  const handleStoreChange = (selectedOptions: any) => {
    const values = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
    updateFilter({ key: "storeCode", value: values });
  }

  const handleRegionalChange = (selectedOptions: any) => {
    const values = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
    updateFilter({ key: "regional", value: values });
  }

  const handleTypeEmailChange = (selectedOption: any) => {
    const value = selectedOption ? selectedOption.value : null;
    updateFilter({ key: "type", value });
  }

  const filterInput = (id: string, label: string, placeholder: string, filterKey?: string) => (
    <div key={id}>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        placeholder={placeholder}
        className="mt-1 h-8 min-h-[32px]"
        onChange={(e) => updateFilter({ key: filterKey || id, value: e.target.value })}
        value={filters?.[(filterKey || id) as keyof typeof filters] || ""}
      />
    </div>
  )

  const renderMultiSelect = (
    id: string,
    label: string,
    options: { value: string | number; label: string }[],
    value: any[],
    onChange: (value: any) => void,
  ) => {
    const hasValue = value && value.length > 0;
    return (
      <div key={id}>
        <Label htmlFor={id}>{label}</Label>
        <div className="mt-1">
          <SelectInput
            options={options}
            placeholder={`Selecione ${label.toLowerCase()}`}
            value={value}
            onChange={onChange}
            className={getSelectClasses(hasValue)}
            classNamePrefix="an-simple-select"
            isMulti
            isClearable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ MultiValue: MultiValueTag }}
            formatOptionLabel={formatOptionLabel}
          />
        </div>
      </div>
    );
  }

  const getSelectedStores = () => {
    if (!filters?.storeCode) return [];
    const codes = Array.isArray(filters.storeCode) ? filters.storeCode : [filters.storeCode];
    return codes.map(code => storeOptions?.find(opt => String(opt.value) === String(code))).filter(Boolean);
  };

  const getSelectedRegionals = () => {
    if (!filters?.regional) return [];
    const regionals = Array.isArray(filters.regional) ? filters.regional : [filters.regional];
    return regionals.map(reg => regionalResponsibleOptions?.find(opt => String(opt.value) === String(reg))).filter(Boolean);
  };

  const getSelectedType = () => {
    if (!filters?.type) return null;
    return EMAIL_TYPE_OPTIONS?.find(opt => String(opt.value) === String(filters.type)) || null;
  };

  const getSelectedStatus = () => {
    if (!filters?.status) return null;
    return ITEM_STATUS_OPTIONS?.find(opt => String(opt.value) === String(filters.status)) || null;
  };

  const getSelectedAggregatedStatus = () => {
    if (!filters?.aggregatedStatus) return null;
    return AGGREGATED_STATUS_OPTIONS?.find(opt => String(opt.value) === String(filters.aggregatedStatus)) || null;
  };

  const handleStatusChange = (selectedOption: any) => {
    const value = selectedOption ? selectedOption.value : null;
    updateFilter({ key: "status", value });
  };

  const handleAggregatedStatusChange = (selectedOption: any) => {
    const value = selectedOption ? selectedOption.value : null;
    updateFilter({ key: "aggregatedStatus", value });
  };

  const renderSingleSelect = (
    id: string,
    label: string,
    options: { value: string | number; label: string }[],
    value: any,
    onChange: (value: any) => void,
  ) => {
    const hasValue = value !== null && value !== undefined;
    return (
      <div key={id}>
        <Label htmlFor={id}>{label}</Label>
        <div className="mt-1">
          <SelectInput
            options={options}
            placeholder={`Selecione ${label.toLowerCase()}`}
            value={value}
            onChange={onChange}
            className={getSelectClasses(hasValue)}
            classNamePrefix="an-simple-select"
            isMulti={false}
            isClearable
            closeMenuOnSelect={true}
            hideSelectedOptions={false}
            formatOptionLabel={formatOptionLabel}
          />
        </div>
      </div>
    );
  };

  const handleDateRangeChange = (startDate: string | undefined, endDate: string | undefined) => {
    const fromValue = startDate ? new Date(startDate).toISOString() : "";
    const toValue = endDate ? new Date(endDate).toISOString() : "";

    updateFilter({ key: "from_date", value: fromValue });
    updateFilter({ key: "to_date", value: toValue });
  };

  const getInitialDateRange = () => {
    if (!filters?.from_date && !filters?.to_date) return undefined;

    try {
      return {
        from: filters.from_date ? new Date(filters.from_date) : undefined,
        to: filters.to_date ? new Date(filters.to_date) : undefined,
      };
    } catch {
      return undefined;
    }
  };

  const filterDateRange = (
    <div key="date_range_filter">
      <Label htmlFor="date_range">Período</Label>
      <div className="mt-1">
        <DateRangePicker
          onDateChange={handleDateRangeChange}
          initialDateRange={getInitialDateRange()}
          placeholder="Selecionar período"
          className="w-full"
        />
      </div>
    </div>
  )


  const handleFinalizedChange = (checked: boolean) => {
    updateFilter({ key: "is_finalized", value: checked })
  }

  const filterFinalizedCheckbox = (
    <div key="finalized_filter" className="flex items-center gap-2 mt-2">
      <Checkbox id="finalized" checked={!!filters?.is_finalized} onCheckedChange={handleFinalizedChange} />
      <Label htmlFor="finalized">Apenas finalizados</Label>
    </div>
  )

  const FILTERS: Record<ConciliationEmailReportType, JSX.Element[]> = {
    [ConciliationEmailReportType.EMAILS_BY_STORE]: [
      filterInput("search", "Buscar", "Buscar...", "search"),
      filterDateRange,
      renderMultiSelect(
        "store",
        "Loja",
        storeOptions || [],
        getSelectedStores(),
        handleStoreChange,
      ),
      renderMultiSelect(
        "regional",
        "Regional",
        regionalResponsibleOptions || [],
        getSelectedRegionals(),
        handleRegionalChange,
      ),
    ],
    [ConciliationEmailReportType.EMAILS_BY_REGIONAL]: [
      filterInput("search", "Buscar", "Buscar...", "search"),
      filterDateRange,
      renderMultiSelect(
        "regional",
        "Regional",
        regionalResponsibleOptions || [],
        getSelectedRegionals(),
        handleRegionalChange,
      ),
    ],
    [ConciliationEmailReportType.DETAILED_DIVERGENCE]: [
      filterInput("operator", "Operador", "Buscar operador...", "operator"),
      filterDateRange,
      renderMultiSelect(
        "store",
        "Loja",
        storeOptions || [],
        getSelectedStores(),
        handleStoreChange,
      ),
      renderMultiSelect(
        "regional",
        "Regional",
        regionalResponsibleOptions || [],
        getSelectedRegionals(),
        handleRegionalChange,
      ),
      renderSingleSelect(
        "status",
        "Status",
        ITEM_STATUS_OPTIONS,
        getSelectedStatus(),
        handleStatusChange,
      ),
      filterFinalizedCheckbox,
    ],
    [ConciliationEmailReportType.DETAILED_NSU]: [
      filterInput("operator", "Operador", "Buscar operador...", "operator"),
      filterDateRange,
      renderMultiSelect(
        "store",
        "Loja",
        storeOptions || [],
        getSelectedStores(),
        handleStoreChange,
      ),
      renderMultiSelect(
        "regional",
        "Regional",
        regionalResponsibleOptions || [],
        getSelectedRegionals(),
        handleRegionalChange,
      ),
      renderSingleSelect(
        "status",
        "Status",
        ITEM_STATUS_OPTIONS,
        getSelectedStatus(),
        handleStatusChange,
      ),
      filterFinalizedCheckbox,
    ],
    [ConciliationEmailReportType.SENT_EMAILS]: [
      filterInput("search", "Buscar", "Buscar...", "search"),
      filterDateRange,
      renderMultiSelect(
        "store",
        "Loja",
        storeOptions || [],
        getSelectedStores(),
        handleStoreChange,
      ),
      renderMultiSelect(
        "regional",
        "Regional",
        regionalResponsibleOptions || [],
        getSelectedRegionals(),
        handleRegionalChange,
      ),
      renderSingleSelect(
        "type",
        "Tipo de E-mail",
        EMAIL_TYPE_OPTIONS,
        getSelectedType(),
        handleTypeEmailChange,
      ),
      renderSingleSelect(
        "aggregatedStatus",
        "Status",
        AGGREGATED_STATUS_OPTIONS,
        getSelectedAggregatedStatus(),
        handleAggregatedStatusChange,
      ),
    ],
    [ConciliationEmailReportType.EMAILS_BY_CONCILIATION]: [
      filterInput("search", "Buscar", "Buscar...", "search"),
      filterDateRange,
    ],
  }

  const handleClearFilters = () => {
    clearAllFilters();
  };

  return (
    <div className="flex items-center gap-4 mx-2">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" className="flex items-center gap-2 h-8 bg-transparent">
            <Filter className="h-4 w-4" />
           Mais Filtros
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-xs p-0 gap-0">
          <SheetHeader className="bg-[#2E0D1E] p-4 text-white">
            <SheetTitle className="text-white">Filtros de Relatórios de E-mail</SheetTitle>
            <SheetDescription className="text-gray-300">
              Selecione o tipo de relatório e ajuste os filtros para visualizar os dados desejados.
            </SheetDescription>
          </SheetHeader>
          <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
            <div>
              <Label htmlFor="report-type-select" className="mb-1 block">
                Tipo de Relatório
              </Label>
              <Select value={listType} onValueChange={handleTypeChange}>
                <SelectTrigger id="report-type-select" className="h-10 w-full mt-2">
                  <SelectValue placeholder="Selecione o tipo de relatório" />
                </SelectTrigger>
                <SelectContent>
                  {LIST_TYPES.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                A seleção do tipo de relatório altera os filtros disponíveis abaixo.
              </p>
            </div>
            <Separator /> {/* Separator after the report type selector */}
            <div className="grid">
              {FILTERS[listType].map((el, i) => (
                <div key={i}>
                  {el}
                  {i < FILTERS[listType].length - 1 && <Separator className="my-4" />} {/* Separator between filters */}
                </div>
              ))}
            </div>
          </div>
          <SheetFooter className="border-t p-4">
            <Button
              variant="outline"
              onClick={handleClearFilters}
              className="w-full flex items-center gap-2 bg-transparent"
            >
              <Trash2 className="h-4 w-4" />
              Limpar Todos os Filtros
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  )
}
